import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FB_LABELS } from '../calendar/eventStyle.js';
import { buildMemberGradient, shortName } from '../calendar/mergeGroupEvents.js';

/**
 * Event body inside the calendar grid, plus the detail panel shown on hover.
 *
 * The panel is rendered in a portal on `document.body` rather than inside the
 * event: FullCalendar clips its cells, so a panel nested in a block is cut off
 * at the cell edge — exactly where it needs to be readable.
 */

function StatusIcon({ code }) {
  const common = { className: 'h-4 w-4 shrink-0', fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', strokeWidth: 2 };
  if (code === 'F') {
    return (
      <svg {...common} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (code === 'B' || code === 'U' || code === 'O') {
    return (
      <svg {...common} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
    );
  }
  return (
    <svg {...common} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

StatusIcon.propTypes = { code: PropTypes.string };

// Two columns and a taller panel, so a large group reads at a glance rather
// than through a long scroll. Beyond this the count says the rest.
const MAX_LISTED_MEMBERS = 60;

// Wait for the pointer to settle before opening: crossing a crowded grid
// otherwise flashed a panel over every block on the way. Long enough to rule
// out a pointer merely passing through, short enough not to feel stuck —
// 700ms read as unresponsive. Once one panel is open, moving to the next
// block is deliberate, so the wait nearly disappears.
const OPEN_DELAY_COLD = 300;
const OPEN_DELAY_WARM = 80;

// Shared by every block: one panel is open at a time, and "is one already
// open" is what decides between the two delays above.
let panelIsOpen = false;

// Fixed width, so the panel can be clamped to the window before it is drawn
// rather than measured after the fact.
const PANEL_WIDTH = 320;
// Rough height above which the panel would overflow the top of the window.
const PANEL_MAX_HEIGHT = 340;

function period(startStr, endStr) {
  const format = (value) =>
    new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${format(startStr)} - ${format(endStr)}`;
}

export default function CustomEventContent({ info }) {
  const [anchor, setAnchor] = useState(null);
  const frameRef = useRef(null);

  const eventTitle = info?.event?.title;
  const extra = info?.event?.extendedProps || {};
  const code = extra.fb?.toUpperCase();
  const statusText = FB_LABELS[code] || 'Inconnu';
  // A merged block stands for several people over the same slot: the subject
  // alone would not say who, so the panel lists them.
  const members = Array.isArray(extra.members) ? extra.members : [];
  const memberColors = Array.isArray(extra.memberColors) ? extra.memberColors : [];
  const others = members.length - 1;
  const subject = extra.title || (members.length > 1 ? '' : eventTitle);

  const closeTimer = useRef(0);
  const openTimer = useRef(0);

  const open = useCallback(() => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    frameRef.current?.closest('.fc-event')?.classList.add('planif-event-active');

    // Keep the panel inside the window: centred on the block by default, but
    // pushed back in when the block sits near an edge — it used to be cut off
    // on the right-hand columns.
    const half = PANEL_WIDTH / 2;
    const margin = 8;
    const centre = rect.left + rect.width / 2;
    const left = Math.min(
      Math.max(centre, half + margin),
      window.innerWidth - half - margin,
    );
    // Above the block, unless there is not enough room — then below it.
    const below = rect.top < PANEL_MAX_HEIGHT + margin;
    // The panel is clamped, the block is not: the caret has to point back at
    // the block, so keep its offset relative to the panel's own left edge.
    const caret = Math.min(Math.max(centre - (left - half), 14), PANEL_WIDTH - 14);
    setAnchor({ left, below, caret, top: below ? rect.bottom + margin : rect.top - margin });
    panelIsOpen = true;
  }, []);

  /** Arm the opening; the pointer has to stay put for it to fire. */
  const scheduleShow = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(open, panelIsOpen ? OPEN_DELAY_WARM : OPEN_DELAY_COLD);
  }, [open]);

  /** Keep an open panel open — used when the pointer moves onto the panel. */
  const keepOpen = useCallback(() => {
    window.clearTimeout(closeTimer.current);
  }, []);

  // A short grace period, so the pointer can travel from the block to the
  // panel without it vanishing on the way — the members list is scrollable and
  // has to be reachable.
  const scheduleHide = useCallback(() => {
    // Cancel a pending opening too: leaving before the delay elapsed means
    // the pointer was only passing through.
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      frameRef.current?.closest('.fc-event')?.classList.remove('planif-event-active');
      setAnchor(null);
      panelIsOpen = false;
    }, 160);
  }, []);

  // The class is set on a node FullCalendar owns, so drop it on unmount too.
  useEffect(() => {
    const frame = frameRef.current;
    return () => {
      window.clearTimeout(closeTimer.current);
      window.clearTimeout(openTimer.current);
      frame?.closest('.fc-event')?.classList.remove('planif-event-active');
    };
  }, []);

  return (
    <div
      ref={frameRef}
      className="fc-event-main-frame"
      onMouseEnter={scheduleShow}
      onMouseLeave={scheduleHide}
    >
      {/* Several people over one slot: a stripe per member down the left edge,
          and a badge with how many others. The subject stays the subject. */}
      {others > 0 && (
        <>
          <span
            aria-hidden="true"
            className="planif-member-band"
            style={{ background: buildMemberGradient(memberColors) }}
          />
          <span className="planif-member-badge">+{others}</span>
        </>
      )}
      {/* Start time only, never the range: with one column per person per day,
          "9:00 - 10:30" is clipped mid-string ("9:00 - 10:3"). The end is said
          by the block's own height, and exactly by the hover panel. */}
      {info.event?.start && !info.event.allDay && (
        <div className="fc-event-time">
          {info.event.start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })}
        </div>
      )}
      <div className="fc-event-title-container">
        <div className="fc-event-title fc-sticky">{eventTitle || ' '}</div>
      </div>
      {anchor &&
        createPortal(
          <div
            role="tooltip"
            onMouseEnter={keepOpen}
            onMouseLeave={scheduleHide}
            /* Centred on the block and clamped to the window; fixed, so the
               grid's own scroll containers never clip it. Pointer events stay
               on: the members list has to be scrollable. */
            className={`planif-hover-panel fixed z-[1400] max-w-[90vw] -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-xl ${anchor.below ? '' : '-translate-y-full'}`}
            style={{ top: `${anchor.top}px`, left: `${anchor.left}px`, width: `${PANEL_WIDTH}px` }}
          >
            {/* Points back at the block it describes — without it the panel
                floated free and you could not tell what it belonged to. */}
            <span
              aria-hidden="true"
              /* Panel below the block -> caret on its top edge, and the
                 other way round. */
              className={`planif-panel-caret ${anchor.below ? 'points-up' : 'points-down'}`}
              style={{ left: `${anchor.caret}px` }}
            />

            <header className="flex items-baseline justify-between gap-2">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                <StatusIcon code={code} />
                {statusText}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-gray-500">
                {period(info.event?.startStr, info.event?.endStr)}
              </span>
            </header>

            {subject && (
              <p className="mt-1.5 text-sm text-gray-800">{subject}</p>
            )}

            {members.length > 0 && (
              <div className="mt-2 border-t border-gray-200 pt-2">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {members.length > 1 ? `${members.length} personnes` : 'Personne'}
                </p>
                {/* grid-cols-2 with min-w-0 + truncate: a long address used to
                    widen the grid and add a horizontal scrollbar. */}
                <ul className="mt-1 grid max-h-56 grid-cols-2 gap-x-3 overflow-y-auto overflow-x-hidden text-sm text-gray-600">
                  {members.slice(0, MAX_LISTED_MEMBERS).map((member) => (
                    <li key={member} className="min-w-0 truncate" title={member}>
                      {shortName(member)}
                    </li>
                  ))}
                </ul>
                {members.length > MAX_LISTED_MEMBERS && (
                  <p className="mt-1 text-xs text-gray-400">
                    et {members.length - MAX_LISTED_MEMBERS} autre
                    {members.length - MAX_LISTED_MEMBERS > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

CustomEventContent.propTypes = {
  info: PropTypes.object.isRequired,
};
